/** @jsxImportSource @emotion/react */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { CellType, DataMap } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { CrudCode } from 'models/common/Edit';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import CustomGrid from 'components/grids/CustomGrid';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';
import { hasRole } from 'utils/SessionUtil';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import UserModal from 'components/modals/common/UserModal';
import { GatingContListPopUp } from '../../gtng/popup/GatingContListPopUp';
import { Employee } from 'models/admin/Employee';
import { MdSpecManager, MdSpecManagerCondition } from 'models/md/MdEquipmentSpec';
import { findSpecManager, saveSpecManager } from 'apis/md/MdEquipmentSpec';

interface DisplayCondition {
  wrtUserNm?: string; // 사양담당자명
}

/**
 * @desc 투자비관리 > 기준정보 > 설비사양 담당자 관리
 *    -> 설비사양 > 설비사양관리 > 설비사양 담당자 관리
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.26   jshwang12       신규 생성
 */
const MdEquipmentSpecManagerPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const initCondition = {
    bildPlntCds: [],
    prdnLnTpCds: [],
    eqclIds: [],
  };
  const [displayCondition, setDisplayCondition] = useState<DisplayCondition>({});
  const [condition, setCondition] = useState<MdSpecManagerCondition>({ ...initCondition });
  const [rowData, setRowData] = useState<MdSpecManager[]>([]);
  const [code, setCode] = useState<any>();
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);
  const [userModalCondition, setUserModalCondition] = useState<any>({});
  const [isOpenUserReadModal, setOpenUserReadModal] = useState<boolean>(false);
  const [userReadModalCondition, setUserReadModalCondition] = useState<any>({});
  const [isOpenSearchUserPopup, setOpenSearchUserPopup] = useState<boolean>(false);

  useEffect(() => {
    getCommonCodes();
    handleSearch();
  }, []);

  useEffect(() => {
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('BILD_PLNT_CD'),
      getCommonCodeNames('MD_PRDN_LN_TP_CD'),
      getCommonCodeNamesCondition({
        cmnGrCd: 'PRDN_PROC_CD',
        optValCtn7: 'Y', // 형개발투자비 산출
      }),
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EQCL_ID',
        optValCtn1: 'Y', // 설비사양대상 (설비기준)
      }),
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EQCL_SPEC_MAPPING',
        optValCtn9: 'Y', // 템플릿업로드(화면표시) 항목여부
      }),
    ]).then(([bildPlntCd, mdPrdnLnTpCd, prdnProcCd, mdEqclId, mdEqclSpecMapping]) => {
      setCode({
        bildPlntCd: bildPlntCd,
        mdPrdnLnTpCd: mdPrdnLnTpCd,
        prdnProcCd: prdnProcCd,
        mdEqclId: mdEqclId,
        mdEqclSpecMapping: mdEqclSpecMapping,
      });
    });
  };

  const handleSearch = () => {
    findSpecManager(condition).then((result) => {
      setRowData(result);
    });
  };

  const handleDisplayConditionChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const name = e.target.name;
    setDisplayCondition((prev) => ({
      ...prev,
      [name]: e.target.value,
    }));

    setCondition((prev) => ({
      ...prev,
      [name.replace('Nm', 'Id')]: '',
    }));
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const validate = () => {
    // 수정사항 없음
    if ((rowData || []).filter((o) => !!o.crudKey).length < 1) {
      return false;
    }
    const valid = [] as string[];
    const pkMap = new Map();

    (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .forEach((o, index) => {
        if (_.isEmpty(o.bildPlntCd)) {
          valid.push(
            `${index + 1} : ${t('md.label.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.prdnLnTpCd)) {
          valid.push(
            `${index + 1} : ${t('md.label.라인유형을 선택해 주세요.', '라인유형을 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.eqclId)) {
          valid.push(
            `${index + 1} : ${t('md.label.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')}`
          );
          return;
        }
        const pkKey = `${o.bildPlntCd}|${o.prdnLnTpCd}|${o.eqclId}`;
        if (pkMap.has(pkKey)) {
          valid.push(
            `${index + 1} : ${t(
              'md.label.중복된 데이터가 존재합니다.',
              '중복된 데이터가 존재합니다.'
            )}`
          );
          return;
        } else {
          pkMap.set(pkKey, index);
        }
      });

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }

    return true;
  };

  /**
   * 저장
   */
  const handleSave = () => {
    if (!validate()) {
      return;
    }
    const confirmMsg = (rowData || []).find((o) => CrudCode.DELETE === o.crudKey)
      ? t(
          'md.label.행삭제 시 선택한 행의 전체 담당자가 삭제됩니다.<br/>변경사항을 저장하시겠습니까?',
          '행삭제 시 선택한 행의 전체 담당자가 삭제됩니다.<br/>변경사항을 저장하시겠습니까?'
        )
      : t('com.label.저장하시겠습니까?', '저장하시겠습니까?');

    openCommonModal({
      modalType: 'confirm',
      content: confirmMsg,
      yesCallback: () => {
        saveSpecManager(rowData).then((res) => {
          openMessageBar({
            type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
            content:
              res?.successOrNot === 'Y'
                ? t('com.label.저장되었습니다.', '저장되었습니다.')
                : res.data && typeof res.data === 'string'
                ? res.data
                : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
          if (res?.successOrNot === 'Y') {
            handleSearch();
          }
        });
      },
    });
  };

  const layoutDefinition = useMemo(() => {
    const mapMdEqclId = new DataMap(code?.mdEqclId || [], 'cmnCd', 'cmnCdNm');
    mapMdEqclId.getDisplayValues = (item) => {
      const targets = (code?.mdEqclSpecMapping || [])
        .filter((o) => o.optValCtn5 === item.prdnLnTpCd)
        .map((o) => o.optValCtn1 || '');
      return (code?.mdEqclId || [])
        .filter((o) => !!o.cmnCdNm && (targets || []).includes(o.cmnCd))
        .reduce((acc, cur) => {
          if (acc.findIndex((o) => o.cmnCd === cur.cmnCd) < 0) {
            acc.push(cur);
          }
          return acc;
        }, [])
        .map((o) => o.cmnCdNm || '');
    };
    return [
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'crudKey',
        minWidth: 40,
        width: 43,
        isReadOnly: true,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'bildPlntCd',
        header: String(t('com.label.플랜트', '플랜트')),
        minWidth: 90,
        width: 154,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.bildPlntCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'prdnLnTpCd',
        header: String(t('md.label.라인유형', '라인유형')),
        minWidth: 80,
        width: 100,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdPrdnLnTpCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'eqclId',
        header: String(t('md.label.설비군', '설비군')),
        minWidth: 100,
        width: 120,
        align: 'center',
        dataMap: mapMdEqclId,
      },
      {
        binding: 'wrtUserNms',
        header: String(t('md.label.사양 담당자', '사양 담당자')),
        width: '*',
        minWidth: 100,
        cssClass: 'WijmoFind',
        align: 'left',
        isReadOnly: true,
        isRequired: false,
        cellTemplate: (params) => `
        <span>${params.value || ''}</span>
        <Button id="wrtUserNms"/> 
      `,
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        align: 'center',
        width: 100,
        minWidth: 80,
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일', '수정일')),
        align: 'center',
        width: 100,
        minWidth: 80,
        isReadOnly: true,
      },
      {
        binding: 'wrtUserIds',
        visible: false,
      },
    ];
  }, [code]);

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.설비사양담당자 목록', '설비사양담당자 목록'),
      t('md.label.설비사양담당자 목록', '설비사양담당자 목록')
    );
  };

  /**
   * 행추가
   */
  const handleAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
    };
    setRowData([newRow, ...rowData]);
  };

  /**
   * 행복사
   */
  const handleCopyRow = () => {
    const selectedRows = gridRef.current?.rows?.filter((r) => r.isSelected);
    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.복사할 행을 선택해 주세요.', '복사할 행을 선택해 주세요.'),
      });
      return;
    }
    const newRows = [] as MdSpecManager[];
    selectedRows.forEach((o) => {
      const item = o.dataItem;
      newRows.push({
        ...item,
        crudKey: CrudCode.CREATE,
        dataInsUserNm: '',
        dataInsDtm: '',
        dataUpdUserNm: '',
        dataUpdDtm: '',
      });
    });
    setRowData([...newRows, ...rowData]);
  };

  /**
   * 행상제
   */
  const handleDelRow = () => {
    const selectedRows = gridRef.current?.rows?.filter((r) => r.isSelected);
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const onBeginningEdit = useEvent((grid, e) => {
    if (!hasAuth) {
      e.cancel = true;
    } else {
      const col = grid.columns[e.col];
      const binding = col.binding;
      const item = grid.rows[e.row].dataItem;
      if (CrudCode.CREATE !== item.crudKey) {
        // 수정 시 키값 수정불가
        if (['bildPlntCd', 'prdnLnTpCd', 'eqclId'].includes(binding)) {
          e.cancel = true;
        }
      }
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;
    // 라인 유형
    if ('prdnLnTpCd' === binding) {
      const cd = (code?.mdPrdnLnTpCd || []).find((o) => o.cmnCdNm === newVal)?.cmnCd || '';
      // 값이 변한 경우 설비군 초기화
      if (oldVal !== cd) {
        for (let i = 0; i < 6; i++) {
          item[`eqclId`] = '';
        }
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (!hasAuth) {
        cell.ariaReadOnly = true;
      } else {
        if (CrudCode.CREATE !== item.crudKey) {
          // 수정 시 키값 수정불가
          if (['bildPlntCd', 'prdnLnTpCd', 'eqclId'].includes(binding)) {
            cell.ariaReadOnly = true;
            if (cell.firstElementChild instanceof HTMLButtonElement) {
              cell.firstChild.remove();
            }
          }
        }
        if ('wrtUserNms' === binding) {
          cell.ariaReadOnly = !hasAuth;
        }
      }
    }
  });

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        const binding = grid.columns[ht.col].binding;
        if (e.target instanceof HTMLButtonElement) {
          const id = e.target.id;
          // 사양 담당자
          if ('wrtUserNms' === id) {
            if (hasAuth) {
              setUserModalCondition({
                wrtUserIds: item?.wrtUserIds || '',
                target: {
                  binding: binding,
                  row: ht.row,
                  col: ht.col,
                },
              });
              setOpenUserModal(true);
            } else {
              setUserReadModalCondition({
                initParam: item?.wrtUserIds,
              });
              setOpenUserReadModal(true);
            }
          }
        }
      }
    });
  });

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('md.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={code?.bildPlntCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCds || []}
                  msgError={String(t('com.msg.플랜트를 선택해 주세요.', '플랜트를 선택해 주세요.'))}
                  onChange={(value) => handleOnChange('bildPlntCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.라인유형', '라인유형'))}</label>
                <MultiComboBox
                  id="prdnLnTpCd"
                  options={code?.mdPrdnLnTpCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.prdnLnTpCds || []}
                  msgError={String(
                    t('md.label.라인유형을 선택해 주세요.', '라인유형을 선택해 주세요.')
                  )}
                  onChange={(value) => handleOnChange('prdnLnTpCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.설비군', '설비군'))}</label>
                <MultiComboBox
                  id="prdnLnTpCd"
                  options={code?.mdEqclId || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.eqclIds || []}
                  msgError={String(
                    t('md.label.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')
                  )}
                  onChange={(value) => handleOnChange('eqclIds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.담당자', '담당자'))}</label>
                <CustomInputWithSearch
                  name="wrtUserNm"
                  className="find"
                  value={displayCondition?.wrtUserNm}
                  placeholder={String(
                    t('md.label.담당자를 입력해 주세요.', '담당자를 입력해 주세요.')
                  )}
                  onChange={handleDisplayConditionChange}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      setOpenSearchUserPopup(true);
                    }
                  }}
                  onSearchClick={() => {
                    setOpenSearchUserPopup(true);
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onReset={() => {
                setDisplayCondition(
                  (prev) =>
                    Object.keys(prev).reduce(
                      (acc, cur) => Object.assign(acc, { [cur]: '' }),
                      {}
                    ) as DisplayCondition
                );
              }}
              onSearch={() => handleSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.설비사양 담당자 목록', '설비사양 담당자 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="copyRow"
                onClick={handleCopyRow}
                disableRipple
              >
                {t('com.button.행복사', '행복사')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`mdEquipmentSpecManagerGrid`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={580}
        frozenColumns={4}
        isFilter={false}
        allowPinning={false}
        allowSorting={false}
        isReadOnly={!hasAuth}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={handleSave} disableRipple>
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}

      {isOpenSearchUserPopup && (
        <UserModal
          open={isOpenSearchUserPopup}
          close={() => setOpenSearchUserPopup(false)}
          title={t('md.label.사양 담당자 조회', '사양 담당자 조회')}
          defaultUserId={condition?.wrtUserId}
          condition={{
            empNm: displayCondition?.wrtUserNm || '',
            roles: ['MD_IVES_MANAGER'],
          }}
          singleSelect={true}
          isOnlyUsed={true}
          onCallback={(result: any) => {
            const user = result as Employee;
            setDisplayCondition((prev) => ({
              ...prev,
              wrtUserNm: user?.empNm || '',
            }));
            setCondition((prev) => ({
              ...prev,
              wrtUserId: user?.userId || '',
            }));
          }}
        />
      )}

      {isOpenUserModal && (
        <UserModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('md.label.사양 담당자 조회', '사양 담당자 조회')}
          defaultUserId={userModalCondition?.wrtUserIds}
          singleSelect={false}
          isOnlyUsed={true}
          onCallback={(result: any) => {
            const target = userModalCondition?.target;
            if (target && target?.row > -1) {
              const users = (result || []) as Employee[];
              const item = gridRef.current?.rows[target.row].dataItem;
              item[target.binding] = users.map((o) => o.empNm).join(', ');
              item['wrtUserIds'] = users.map((o) => o.userId).join(', ');
              if (item.crudKey !== CrudCode.CREATE) {
                item.crudKey = CrudCode.UPDATE;
              }
              gridRef.current?.collectionView.refresh();
            }
          }}
        />
      )}

      {isOpenUserReadModal && (
        <GatingContListPopUp
          open={isOpenUserReadModal}
          close={() => setOpenUserReadModal(false)}
          title={t('md.label.사양 담당자', '사양 담당자')}
          initParam={userReadModalCondition?.initParam}
        />
      )}
    </>
  );
};

export default MdEquipmentSpecManagerPage;
